import { useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ScrollArea } from '../ui/scroll-area';
import LocationImage from '../LocationImage';
import { itineraryApi } from '../../services/api';
import { getString, renderEstimatedCost, renderRecommendations, createGoogleMapsLink } from './helpers';
import { toast } from 'sonner';
import {
  Sun, Sunset, Moon, MapPin, Camera, Utensils, Plane,
  ExternalLink, Images, Car, Users, Ticket, Navigation, ChevronDown, ChevronUp,
  Landmark, Trees, Building2, Store, Church, Coffee, Eye, History, Flower2, Theater,
  Edit3, Save, X, Plus, Ruler,
} from 'lucide-react';

const TYPE_ICONS = {
  landmark: Landmark, park: Trees, museum: Building2, market: Store,
  temple: Church, cafe: Coffee, viewpoint: Eye, historic: History,
  garden: Flower2, cultural: Theater,
};

const getTimeIcon = (time) => {
  switch (time) {
    case 'morning': return <Sun className="w-4 h-4 text-burnt" />;
    case 'afternoon': return <Sunset className="w-4 h-4 text-burnt-light" />;
    case 'evening': return <Moon className="w-4 h-4 text-forest" />;
    default: return null;
  }
};

/* ─── Nearby Places Component with Distance + Add to Itinerary ─── */
const NearbyPlaces = ({ locationText, city, country, onAddPlace }) => {
  const [places, setPlaces] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [distance, setDistance] = useState(5);

  const fetchNearby = useCallback(async () => {
    if (places && !loading) { setExpanded(prev => !prev); return; }
    setLoading(true);
    setExpanded(true);
    try {
      const res = await itineraryApi.getNearbyPlaces(locationText, city, country);
      setPlaces(res.data.places || []);
    } catch {
      setPlaces([]);
    } finally {
      setLoading(false);
    }
  }, [locationText, city, country, places, loading]);

  const refetchWithDistance = useCallback(async () => {
    setLoading(true);
    try {
      const res = await itineraryApi.getNearbyPlaces(locationText, city, country);
      const allPlaces = res.data.places || [];
      // Filter by distance - parse "X.X km" format
      const filtered = allPlaces.filter(p => {
        const d = parseFloat(p.distance);
        return !isNaN(d) && d <= distance;
      });
      setPlaces(filtered.length > 0 ? filtered : allPlaces);
    } catch {
      setPlaces([]);
    } finally {
      setLoading(false);
    }
  }, [locationText, city, country, distance]);

  return (
    <div className="mt-3 pt-2 border-t border-sky/10">
      <button
        onClick={fetchNearby}
        className="inline-flex items-center gap-1.5 text-xs text-ocean hover:text-ocean-light transition-colors font-semibold bg-sky/5 hover:bg-sky/10 px-3 py-1.5 rounded-full"
        data-testid="nearby-places-toggle"
      >
        <Navigation className="w-3.5 h-3.5" />
        Nearby places to check
        {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
      </button>

      {expanded && (
        <div className="mt-2 space-y-1.5" data-testid="nearby-places-list">
          {/* Distance filter */}
          <div className="flex items-center gap-2 px-3 py-1.5 bg-gold/5 rounded-lg">
            <Ruler className="w-3.5 h-3.5 text-gold" />
            <span className="text-[10px] text-ocean/60 font-medium">Within</span>
            <Input
              type="number"
              min={1}
              max={50}
              value={distance}
              onChange={(e) => setDistance(Number(e.target.value) || 5)}
              className="w-14 h-6 text-xs text-center p-0 border-ocean/20"
              data-testid="nearby-distance-input"
            />
            <span className="text-[10px] text-ocean/60 font-medium">km</span>
            <button
              onClick={refetchWithDistance}
              className="text-[10px] text-ocean font-semibold bg-ocean/10 hover:bg-ocean/20 px-2 py-0.5 rounded-full transition-colors"
              data-testid="nearby-distance-apply"
            >
              Apply
            </button>
          </div>

          {loading ? (
            <div className="flex items-center gap-2 py-2 px-3 bg-sky/5 rounded-lg">
              <div className="w-3 h-3 border-2 border-ocean/40 border-t-transparent rounded-full animate-spin" />
              <span className="text-xs text-ocean/60">Finding nearby places...</span>
            </div>
          ) : places && places.length > 0 ? (
            places.map((place, i) => {
              const PlaceIcon = TYPE_ICONS[place.type] || MapPin;
              return (
                <div
                  key={`${place.name}-${place.type}`}
                  className="flex items-center gap-2.5 py-2 px-3 bg-sky/5 hover:bg-sky/10 rounded-lg transition-colors group"
                  data-testid={`nearby-place-${i}`}
                >
                  <div className="w-7 h-7 rounded-lg bg-ocean/10 flex items-center justify-center flex-shrink-0">
                    <PlaceIcon className="w-3.5 h-3.5 text-ocean" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-charcoal truncate">{place.name}</p>
                    <p className="text-[10px] text-charcoal/50 truncate">{place.description}</p>
                  </div>
                  <span className="text-[10px] text-ocean/50 whitespace-nowrap flex-shrink-0">{place.distance}</span>
                  {onAddPlace && (
                    <button
                      onClick={(e) => { e.stopPropagation(); onAddPlace(place); }}
                      className="w-7 h-7 rounded-full bg-gold hover:bg-gold-light text-ocean flex items-center justify-center flex-shrink-0 transition-colors shadow-sm group/add relative"
                      data-testid={`add-nearby-${i}`}
                    >
                      <Plus className="w-3.5 h-3.5" />
                      <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-ocean text-white text-[9px] font-medium px-2 py-1 rounded-md whitespace-nowrap opacity-0 group-hover/add:opacity-100 transition-opacity pointer-events-none shadow-lg">
                        Add to itinerary
                        <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-ocean" />
                      </span>
                    </button>
                  )}
                  <a href={createGoogleMapsLink(`${place.name}, ${city}`)} target="_blank" rel="noopener noreferrer" className="flex-shrink-0 group/map relative">
                    <ExternalLink className="w-3 h-3 text-ocean/30 group-hover/map:text-ocean/60" />
                    <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-ocean text-white text-[9px] font-medium px-2 py-1 rounded-md whitespace-nowrap opacity-0 group-hover/map:opacity-100 transition-opacity pointer-events-none shadow-lg">
                      View on Google Maps
                      <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-ocean" />
                    </span>
                  </a>
                </div>
              );
            })
          ) : (
            <p className="text-xs text-charcoal/40 py-2 px-3">No nearby alternatives found</p>
          )}
        </div>
      )}
    </div>
  );
};

/* ─── Inline Activity Editor ─── */
const ActivityEditor = ({ activity, timeOfDay, onSave, onCancel }) => {
  const actText = typeof activity === 'object' ? (activity.activity || activity.description || '') : (activity || '');
  const locText = typeof activity === 'object' ? (activity.location || '') : '';

  const [editActivity, setEditActivity] = useState(actText);
  const [editLocation, setEditLocation] = useState(locText);

  return (
    <div className="p-4 bg-gold/5 border border-gold/20 rounded-lg space-y-3" data-testid={`edit-form-${timeOfDay}`}>
      <div>
        <label className="text-xs font-semibold text-ocean/70 mb-1 block">Activity</label>
        <textarea
          value={editActivity}
          onChange={e => setEditActivity(e.target.value)}
          className="w-full h-20 p-2 text-sm rounded-lg border border-ocean/20 bg-white/80 text-charcoal resize-none focus:outline-none focus:ring-2 focus:ring-gold/30"
          data-testid={`edit-activity-text-${timeOfDay}`}
        />
      </div>
      <div>
        <label className="text-xs font-semibold text-ocean/70 mb-1 block">Location</label>
        <Input
          value={editLocation}
          onChange={e => setEditLocation(e.target.value)}
          className="h-9 text-sm bg-white/80 border-ocean/20"
          placeholder="Location name"
          data-testid={`edit-location-text-${timeOfDay}`}
        />
      </div>
      <div className="flex gap-2 justify-end">
        <Button size="sm" variant="ghost" onClick={onCancel} className="text-charcoal/50 h-8 px-3 rounded-full" data-testid={`edit-cancel-${timeOfDay}`}>
          <X className="w-3.5 h-3.5 mr-1" /> Cancel
        </Button>
        <Button size="sm" onClick={() => onSave(editActivity, editLocation)} className="bg-gold hover:bg-gold-light text-ocean h-8 px-4 rounded-full font-semibold" data-testid={`edit-save-${timeOfDay}`}>
          <Save className="w-3.5 h-3.5 mr-1" /> Save
        </Button>
      </div>
    </div>
  );
};

/* ─── Main Schedule Tab ─── */
export const ScheduleTab = ({ generatedItinerary, openGallery, handleBookFlight, tripInput, itineraryId, onItineraryUpdate }) => {
  const city = tripInput?.destination_city || '';
  const country = tripInput?.destination_country || '';
  const [editingSlot, setEditingSlot] = useState(null); // "dayIdx-timeOfDay"
  const [saving, setSaving] = useState(false);

  const handleSaveEdit = async (dayIdx, timeOfDay, newText, newLocation) => {
    if (!itineraryId || !generatedItinerary?.days) return;
    setSaving(true);
    try {
      const updatedDays = [...generatedItinerary.days];
      const day = { ...updatedDays[dayIdx] };
      const oldActivity = day[timeOfDay];

      if (typeof oldActivity === 'object') {
        day[timeOfDay] = { ...oldActivity, activity: newText, description: newText, location: newLocation };
      } else {
        day[timeOfDay] = { activity: newText, description: newText, location: newLocation };
      }
      updatedDays[dayIdx] = day;

      const updatedItinerary = { ...generatedItinerary, days: updatedDays };
      await itineraryApi.save(itineraryId, updatedItinerary);
      onItineraryUpdate?.(updatedItinerary);
      setEditingSlot(null);
      toast.success('Activity updated');
    } catch {
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleAddNearbyPlace = async (dayIdx, timeOfDay, place) => {
    if (!itineraryId || !generatedItinerary?.days) return;
    try {
      const updatedDays = [...generatedItinerary.days];
      const day = { ...updatedDays[dayIdx] };

      // Add as a new activity text appended to existing
      const oldActivity = day[timeOfDay];
      const addText = `\n\nAlso visit: ${place.name} - ${place.description} (${place.distance})`;

      if (typeof oldActivity === 'object') {
        const existing = oldActivity.activity || oldActivity.description || '';
        day[timeOfDay] = { ...oldActivity, activity: existing + addText, description: existing + addText };
      } else {
        day[timeOfDay] = (oldActivity || '') + addText;
      }
      updatedDays[dayIdx] = day;

      const updatedItinerary = { ...generatedItinerary, days: updatedDays };
      await itineraryApi.save(itineraryId, updatedItinerary);
      onItineraryUpdate?.(updatedItinerary);
      toast.success(`${place.name} added to Day ${dayIdx + 1} ${timeOfDay}`);
    } catch {
      toast.error('Failed to add place');
    }
  };

  return (
    <>
      <ScrollArea className="h-[600px] pr-4">
        <div className="space-y-6">
          {/* Day 1 Airport Pickup Banner */}
          <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-white/30 shadow-xl p-4 flex items-center gap-4" data-testid="airport-pickup-banner">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-green-500 to-green-600 flex items-center justify-center flex-shrink-0">
              <Car className="w-5 h-5 text-white" />
            </div>
            <div className="flex-1">
              <p className="text-ocean font-semibold text-sm">Need an airport pickup?</p>
              <p className="text-ocean/60 text-xs">Pre-book a professional driver for a stress-free arrival</p>
            </div>
            <div className="flex gap-2 flex-shrink-0">
              <a href="https://tpm.lv/sD3CoTaI" target="_blank" rel="noopener noreferrer">
                <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white rounded-full text-xs h-8 px-3">
                  Welcome Pickups <ExternalLink className="w-3 h-3 ml-1" />
                </Button>
              </a>
              <a href="https://gettransfer.tpm.lv/CDo528tq" target="_blank" rel="noopener noreferrer">
                <Button size="sm" className="bg-ocean hover:bg-ocean-light text-white rounded-full text-xs h-8 px-3">
                  GetTransfer <ExternalLink className="w-3 h-3 ml-1" />
                </Button>
              </a>
              <a href="https://kiwitaxi.tpm.lv/oSXEVK28" target="_blank" rel="noopener noreferrer">
                <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-white rounded-full text-xs h-8 px-3">
                  Kiwitaxi <ExternalLink className="w-3 h-3 ml-1" />
                </Button>
              </a>
            </div>
          </div>

          {Array.isArray(generatedItinerary.days) ? (
            generatedItinerary.days.map((day, index) => (
              <motion.div
                key={day.day || `day-${index}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                className="itinerary-day"
                data-testid={`day-${index + 1}`}
              >
                <div className="day-marker bg-gold text-ocean shadow-lg">{index + 1}</div>
                <Card className="bg-white/95 backdrop-blur-xl border-white/30 shadow-xl hover:shadow-2xl transition-all duration-300">
                  <CardHeader className="pb-2 bg-gradient-to-r from-ocean/10 via-sky/5 to-gold/10 rounded-t-lg">
                    <CardTitle className="font-serif text-xl text-ocean">
                      Day {index + 1}: {getString(day.title) || getString(day.date) || `Day ${index + 1}`}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    {['morning', 'afternoon', 'evening'].map((timeOfDay, timeIdx) => {
                      const activity = day[timeOfDay];
                      if (!activity) return null;
                      
                      let activityText = '';
                      if (typeof activity === 'string') {
                        activityText = activity;
                      } else if (typeof activity === 'object') {
                        activityText = activity.description || activity.activity || activity.details || '';
                      }
                      if (!activityText || activityText === 'N/A' || activityText.toLowerCase() === 'n/a') return null;
                      
                      const locationText = (typeof activity === 'object' && activity.location) 
                        ? getString(activity.location) : null;

                      const slotKey = `${index}-${timeOfDay}`;
                      const isEditing = editingSlot === slotKey;
                      
                      return (
                        <div key={timeOfDay} className="mb-4">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              {getTimeIcon(timeOfDay)}
                              <p className="text-xs uppercase tracking-wide text-charcoal/60 font-semibold">
                                {timeOfDay}
                              </p>
                            </div>
                            {!isEditing && (
                              <button
                                onClick={() => setEditingSlot(slotKey)}
                                className="inline-flex items-center gap-1 text-[10px] text-white bg-gold hover:bg-gold-light px-2.5 py-1 rounded-full transition-colors font-semibold shadow-sm"
                                data-testid={`edit-btn-day${index + 1}-${timeOfDay}`}
                              >
                                <Edit3 className="w-3 h-3" /> Edit
                              </button>
                            )}
                          </div>

                          {isEditing ? (
                            <ActivityEditor
                              activity={activity}
                              timeOfDay={timeOfDay}
                              onSave={(text, loc) => handleSaveEdit(index, timeOfDay, text, loc)}
                              onCancel={() => setEditingSlot(null)}
                            />
                          ) : (
                            <div className="bg-white/90 backdrop-blur-sm rounded-lg border border-sky/20 hover:shadow-md transition-all duration-300 overflow-hidden">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                                <div className="h-48 overflow-hidden bg-gray-100">
                                  <LocationImage 
                                    query={locationText || activityText?.split(',')[0]?.split('.')[0]?.trim()}
                                    type="activity"
                                    index={index * 3 + timeIdx}
                                    className="w-full h-full object-cover"
                                    alt={locationText || activityText}
                                  />
                                </div>
                                <div className="p-5">
                                  <p className="text-charcoal text-base leading-relaxed mb-3">
                                    {activityText}
                                  </p>
                                  {locationText && (
                                    <a
                                      href={createGoogleMapsLink(locationText)}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="inline-flex items-center gap-2 text-sm text-white bg-ocean hover:bg-ocean-light px-4 py-2 rounded-full transition-colors"
                                    >
                                      <MapPin className="w-4 h-4" />
                                      <span>View on Google Maps</span>
                                      <ExternalLink className="w-3 h-3" />
                                    </a>
                                  )}
                                  <button
                                    onClick={() => openGallery(locationText || activityText?.split(',')[0]?.split('.')[0]?.trim())}
                                    className="inline-flex items-center gap-2 text-sm text-ocean bg-sky/10 hover:bg-sky/20 px-4 py-2 rounded-full transition-colors mt-2 ml-0 sm:mt-0 sm:ml-2"
                                    data-testid={`view-photos-btn-day${index + 1}-${timeOfDay}`}
                                  >
                                    <Images className="w-4 h-4" />
                                    <span>View Photos</span>
                                  </button>
                                  {typeof activity === 'object' && activity.tip && (
                                    <p className="text-sm text-gold mt-3 italic bg-gold/10 p-3 rounded-lg">
                                      Tip: {getString(activity.tip)}
                                    </p>
                                  )}
                                  {typeof activity === 'object' && activity.estimated_cost && (
                                    <p className="text-sm text-ocean mt-2 font-medium">
                                      Cost: {renderEstimatedCost(activity.estimated_cost)}
                                    </p>
                                  )}
                                  <NearbyPlaces
                                    locationText={locationText || activityText?.split(',')[0]?.split('.')[0]?.trim()}
                                    city={city}
                                    country={country}
                                    onAddPlace={(place) => handleAddNearbyPlace(index, timeOfDay, place)}
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })}
                    
                    {day.activities && Array.isArray(day.activities) && (
                      <div className="space-y-3">
                        {day.activities.map((activity, i) => (
                          <div key={`act-${getString(activity.name || activity.title || '').slice(0, 15) || i}`} className="p-3 bg-sand/50 rounded-lg">
                            <p className="text-charcoal font-medium">{getString(activity.name || activity.title || activity.activity || activity)}</p>
                            {activity.time && <p className="text-xs text-charcoal/60">{getString(activity.time)}</p>}
                            {activity.description && <p className="text-sm text-charcoal/70 mt-1">{getString(activity.description)}</p>}
                            {activity.location && (
                              <p className="text-sm text-charcoal/70 flex items-center gap-1 mt-1">
                                <MapPin className="w-3 h-3" />
                                {getString(activity.location)}
                              </p>
                            )}
                            {activity.estimated_cost && (
                              <p className="text-sm text-forest mt-1">
                                Cost: {renderEstimatedCost(activity.estimated_cost)}
                              </p>
                            )}
                            <NearbyPlaces
                              locationText={getString(activity.name || activity.title || activity.location || activity.activity || '')}
                              city={city}
                              country={country}
                              onAddPlace={null}
                            />
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {day.restaurants && (Array.isArray(day.restaurants) ? day.restaurants.length > 0 : day.restaurants) && (
                      <div className="pt-3 border-t border-sand-dark">
                        <p className="text-xs uppercase tracking-wide text-charcoal/60 mb-2 flex items-center gap-1">
                          <Utensils className="w-3 h-3" />
                          Recommended Restaurants
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {Array.isArray(day.restaurants) ? (
                            day.restaurants.map((restaurant, i) => (
                              <span key={`rest-${getString(restaurant).slice(0, 20) || i}`} className="px-3 py-1 bg-burnt/10 text-burnt rounded-full text-xs">
                                {getString(restaurant)}
                              </span>
                            ))
                          ) : (
                            <span className="text-sm text-charcoal">{getString(day.restaurants)}</span>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {day.attractions && (Array.isArray(day.attractions) ? day.attractions.length > 0 : day.attractions) && (
                      <div className="pt-3 border-t border-sand-dark">
                        <p className="text-xs uppercase tracking-wide text-charcoal/60 mb-2 flex items-center gap-1">
                          <Camera className="w-3 h-3" />
                          Attractions
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {Array.isArray(day.attractions) ? (
                            day.attractions.map((attraction, i) => (
                              <span key={`attr-${getString(attraction).slice(0, 20) || i}`} className="px-3 py-1 bg-forest/10 text-forest rounded-full text-xs">
                                {getString(attraction)}
                              </span>
                            ))
                          ) : (
                            <span className="text-sm text-charcoal">{getString(day.attractions)}</span>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {day.recommendations && (
                      <div className="pt-3 border-t border-sand-dark">
                        <p className="text-xs uppercase tracking-wide text-charcoal/60 mb-2">Recommendations</p>
                        {renderRecommendations(day.recommendations)}
                      </div>
                    )}
                    
                    {day.estimated_cost && (
                      <div className="pt-3 border-t border-sand-dark">
                        <p className="text-xs uppercase tracking-wide text-charcoal/60 mb-1">Estimated Cost</p>
                        <p className="text-forest font-medium">{renderEstimatedCost(day.estimated_cost)}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ))
          ) : generatedItinerary.days ? (
            <Card className="bg-white/90 backdrop-blur-sm border-sky/20 p-4">
              <p className="text-charcoal/60 mb-2">Schedule data:</p>
              <pre className="text-sm bg-sand/50 p-4 rounded-lg overflow-auto whitespace-pre-wrap">
                {JSON.stringify(generatedItinerary.days, null, 2)}
              </pre>
            </Card>
          ) : (
            <p className="text-charcoal/60">No schedule available</p>
          )}
        </div>
      </ScrollArea>

      {/* Attractions Booking Banner */}
      <div className="mt-4 bg-white/95 backdrop-blur-xl rounded-2xl border border-white/30 shadow-xl p-4 flex items-center gap-4" data-testid="attractions-booking-banner">
        <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-orange-500 to-orange-600 flex items-center justify-center flex-shrink-0">
          <Ticket className="w-5 h-5 text-white" />
        </div>
        <div className="flex-1">
          <p className="text-ocean font-semibold text-sm">Skip-the-line tickets to attractions</p>
          <p className="text-ocean/60 text-xs">Book museums, tours, and experiences with instant confirmation</p>
        </div>
        <a href="https://tiqets.tpm.lv/NnUJBB3c" target="_blank" rel="noopener noreferrer">
          <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white rounded-full text-xs h-8 px-3" data-testid="tiqets-btn">
            Tiqets <ExternalLink className="w-3 h-3 ml-1" />
          </Button>
        </a>
      </div>
      
      <a href="https://www.travelean.com" target="_blank" rel="noopener noreferrer" className="mt-4 block">
        <div className="bg-ocean hover:bg-ocean-light text-white rounded-2xl p-4 flex items-center justify-center gap-3 shadow-xl transition-all duration-200 hover:shadow-2xl cursor-pointer" data-testid="book-flight-btn">
          <Plane className="w-5 h-5" />
          <span className="font-sans font-semibold text-sm">Book Flight on Travelean</span>
          <ExternalLink className="w-4 h-4" />
        </div>
      </a>
    </>
  );
};
